// components/modals/MeetingsContent.js

import React, { useState, useEffect, useMemo } from 'react';
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiLoader,
  FiCalendar,
  FiClock,
  FiUsers,
  FiRefreshCw,
  FiSearch,
  FiCheckCircle,
  FiXCircle,
  FiPlayCircle,
  FiServer,
} from 'react-icons/fi';
import toast from 'react-hot-toast';

import { DataState } from '../../context/DataProvider';
import MeetingFormModal from './MeetingFormModal';
import * as meetingsApi from '../../services/meetingsApi';

const MeetingsContent = () => {
  const { userGoogleId: accessToken } = DataState();
  const [meetings, setMeetings] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  // --- UI State for Search ---
  const [searchQuery, setSearchQuery] = useState('');

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [meetingsData, usersData] = await Promise.all([
        meetingsApi.listMeetings(accessToken),
        meetingsApi.listUsers(accessToken),
      ]);
      setMeetings(meetingsData);
      setAllUsers(usersData);
    } catch (err) {
      toast.error('Failed to load meetings or users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const handleAddMeeting = () => {
    setEditingMeeting({
      title: '',
      description: '',
      startTime: new Date().toISOString().slice(0, 16),
      duration: 60,
      attendees: [], // RENAMED
      organizer: null, // +++ ADDED
      isRecurring: false,
      meetingEvents: [],
    });
    setIsFormOpen(true);
  };

  const handleEditMeeting = (meeting) => {
    // setEditingMeeting(meeting);
    const formattedMeeting = {
      ...meeting,
      // Format attendees list for Multi-Select
      attendees: meeting.attendees.map((p) => ({
        ...p,
        value: p.id,
        label: p.displayName,
      })),
      // Format organizer for Single-Select
      organizer: meeting.organizer
        ? {
            ...meeting.organizer,
            value: meeting.organizer.id,
            label: meeting.organizer.displayName,
          }
        : null,
    };
    setEditingMeeting(formattedMeeting);
    setIsFormOpen(true);
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to delete this meeting series?'))
      return;
    setProcessingId(meetingId);
    try {
      await meetingsApi.deleteMeeting(accessToken, meetingId);
      setMeetings((prev) => prev.filter((m) => m.id !== meetingId));
      toast.success('Meeting deleted.');
    } catch (err) {
      toast.error('Failed to delete meeting.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleSaveMeeting = (updatedMeeting) => {
    // This function is now just for updating the list,
    // the API call will happen inside MeetingFormModal
    setMeetings((prev) =>
      prev.map((m) => (m.id === updatedMeeting.id ? updatedMeeting : m)),
    );

    // If it's a new meeting, add it
    if (!meetings.find((m) => m.id === updatedMeeting.id)) {
      setMeetings((prev) => [updatedMeeting, ...prev]);
    }

    setIsFormOpen(false);
    setEditingMeeting(null);
  };

  const formatDateTime = (isoString) => {
    return new Date(isoString).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  // --- Filter Logic ---
  const filteredMeetings = useMemo(() => {
    if (!searchQuery) return meetings;
    const lowerQuery = searchQuery.toLowerCase();
    return meetings.filter(
      (m) =>
        m.title.toLowerCase().includes(lowerQuery) ||
        (m.description && m.description.toLowerCase().includes(lowerQuery)),
    );
  }, [meetings, searchQuery]);

  // --- HELPER COMPONENT: Status Badge ---
  const StatusBadge = ({ count, type, label, icon: Icon }) => {
    if (!count) return null;

    const styles = {
      processed: 'text-emerald-400 bg-emerald-950/30 border-emerald-900/50',
      queued: 'text-cyan-400 bg-cyan-950/30 border-cyan-900/50',
      processing: 'text-amber-400 bg-amber-950/30 border-amber-900/50',
      reprocessing: 'text-orange-400 bg-orange-950/30 border-orange-900/50',
      pending: 'text-blue-400 bg-blue-950/30 border-blue-900/50',
      failed: 'text-red-400 bg-red-950/30 border-red-900/50',
    };

    return (
      <div
        className={`flex items-center gap-1 rounded border px-1.5 py-0.5 text-[9px] font-medium ${styles[type] || styles.pending}`}
      >
        <Icon className="h-2.5 w-2.5" />
        <span>
          {count} {label}
        </span>
      </div>
    );
  };

  // --- UPDATED SUMMARY: Counts statuses of Meeting Events ---
  const getEventSummary = (events) => {
    if (!events || events.length === 0) {
      return (
        <span className="text-[10px] italic text-zinc-600">
          No events recorded
        </span>
      );
    }
    const counts = events.reduce((acc, e) => {
      acc[e.status] = (acc[e.status] || 0) + 1;
      return acc;
    }, {});

    return (
      <div className="mt-1 flex flex-wrap gap-1.5">
        <StatusBadge
          count={counts.processed}
          type="processed"
          label="Done"
          icon={FiCheckCircle}
        />
        <StatusBadge
          count={counts.queued}
          type="queued"
          label="Queued"
          icon={FiServer}
        />
        <StatusBadge
          count={counts.processing}
          type="processing"
          label="Running"
          icon={FiLoader}
        />
        <StatusBadge
          count={counts.reprocessing}
          type="reprocessing"
          label="Retrying"
          icon={FiRefreshCw}
        />
        <StatusBadge
          count={counts.pending}
          type="pending"
          label="Pending"
          icon={FiPlayCircle}
        />
        <StatusBadge
          count={counts.failed}
          type="failed"
          label="Failed"
          icon={FiXCircle}
        />
      </div>
    );
  };

  return (
    <div className="flex h-full max-h-screen flex-col p-1">
      {/* --- Modals --- */}
      {isFormOpen && (
        <MeetingFormModal
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSave={handleSaveMeeting} // This callback updates the list
          meeting={editingMeeting}
          allUsers={allUsers}
          // dummyApi={dummyApi} // Pass the api object
          accessToken={accessToken}
        />
      )}

      {/* --- Compact Header --- */}
      <div className="mb-3 flex flex-col items-center justify-between gap-3 px-1 md:flex-row">
        <div>
          <h3 className="flex items-center gap-2 text-base font-bold text-zinc-100">
            <FiCalendar className="h-4 w-4 text-purple-500" />
            Meetings
          </h3>
          <p className="text-[10px] text-zinc-500">
            Manage recurring series and transcripts.
          </p>
        </div>

        <div className="flex w-full items-center gap-2 md:w-auto">
          <button
            onClick={() => fetchAllData()}
            disabled={loading}
            className="rounded border border-zinc-700 bg-zinc-800/50 p-1.5 text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-100 disabled:opacity-50"
            title="Refresh"
          >
            <FiRefreshCw
              className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`}
            />
          </button>

          <button
            onClick={handleAddMeeting}
            className="flex items-center gap-1.5 rounded bg-blue-600 px-2.5 py-1.5 text-[11px] font-medium text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-[#5652CF]"
          >
            <FiPlus className="h-3 w-3" /> Add Meeting
          </button>
        </div>
      </div>

      {/* --- Content Card --- */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-zinc-800 bg-[#121214] shadow-sm">
        {/* Slim Toolbar */}
        <div className="flex items-center gap-3 border-b border-zinc-800 bg-zinc-900/30 px-3 py-2">
          <div className="relative max-w-xs flex-1">
            <FiSearch className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Search meetings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded border border-zinc-700 bg-zinc-900 py-1.5 pl-8 pr-2 text-[11px] text-zinc-200 transition-all placeholder:text-zinc-600 focus:border-[#5652CF] focus:outline-none focus:ring-1 focus:ring-[#5652CF]/50"
            />
          </div>
          <div className="ml-auto hidden text-[10px] text-zinc-500 md:block">
            <span className="font-semibold text-zinc-300">
              {meetings.length}
            </span>{' '}
            Series
          </div>
        </div>

        {/* Meetings List Container */}
        <div className="custom-scrollbar flex-1 space-y-2 overflow-auto bg-zinc-900/10 p-2">
          {loading ? (
            <div className="flex h-32 items-center justify-center">
              <FiLoader className="animate-spin text-xl text-[#5652CF]" />
            </div>
          ) : filteredMeetings.length === 0 ? (
            <div className="py-12 text-center">
              <div className="mb-2 inline-block rounded-full bg-zinc-800/50 p-2">
                <FiCalendar className="h-5 w-5 text-zinc-600" />
              </div>
              <p className="text-xs font-medium text-zinc-500">
                No meetings found.
              </p>
              <p className="mt-0.5 text-[10px] text-zinc-600">
                Click "Add Meeting" to get started.
              </p>
            </div>
          ) : (
            filteredMeetings
              .sort((a, b) => new Date(b.startTime) - new Date(a.startTime))
              .map((meeting) => (
                <div
                  key={meeting.id}
                  className="group rounded-md border border-zinc-800 bg-zinc-800/40 p-3 transition-all duration-200 hover:border-zinc-700 hover:bg-zinc-800/60"
                >
                  <div className="mb-2 flex items-start justify-between">
                    <div className="min-w-0 flex-1 pr-3">
                      <div className="mb-0.5 flex items-center gap-2">
                        <h3 className="truncate text-xs font-bold text-zinc-200">
                          {meeting.title}
                        </h3>
                        {meeting.isRecurring && (
                          <span className="flex items-center gap-0.5 rounded border border-purple-900/50 bg-purple-900/30 px-1 py-0.5 text-[9px] font-medium text-purple-400">
                            <FiRefreshCw className="h-2 w-2" /> Recurring
                          </span>
                        )}
                      </div>
                      <p className="line-clamp-1 text-[10px] text-zinc-500">
                        {meeting.description || 'No description provided.'}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <button
                        onClick={() => handleEditMeeting(meeting)}
                        title="Edit"
                        className="rounded p-1 text-zinc-400 transition-colors hover:bg-[#5652CF]/10 hover:text-blue-400"
                      >
                        <FiEdit className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => handleDeleteMeeting(meeting.id)}
                        disabled={processingId === meeting.id}
                        title="Delete"
                        className="rounded p-1 text-zinc-400 transition-colors hover:bg-red-500/10 hover:text-red-400 disabled:cursor-wait"
                      >
                        {processingId === meeting.id ? (
                          <FiLoader className="h-3 w-3 animate-spin" />
                        ) : (
                          <FiTrash2 className="h-3 w-3" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4">
                    <div
                      className="flex items-center gap-1.5 rounded border border-zinc-800/50 bg-zinc-900/50 px-2 py-1 text-[10px] text-zinc-400"
                      title="Start Time"
                    >
                      <FiCalendar className="h-3 w-3 text-zinc-500" />
                      <span className="truncate">
                        {formatDateTime(meeting.startTime)}
                      </span>
                    </div>
                    <div
                      className="flex items-center gap-1.5 rounded border border-zinc-800/50 bg-zinc-900/50 px-2 py-1 text-[10px] text-zinc-400"
                      title="Duration"
                    >
                      <FiClock className="h-3 w-3 text-zinc-500" />
                      <span>{meeting.duration} min</span>
                    </div>
                    <div
                      className="flex items-center gap-1.5 rounded border border-zinc-800/50 bg-zinc-900/50 px-2 py-1 text-[10px] text-zinc-400 md:col-span-2"
                      title="Attendees"
                    >
                      <FiUsers className="h-3 w-3 text-zinc-500" />
                      <span className="truncate">
                        {meeting.attendees?.length || 0} attendees
                      </span>
                    </div>
                  </div>

                  <div className="border-t border-zinc-800/50 pt-2">
                    <div className="mb-1 flex items-center gap-2">
                      <h4 className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">
                        Processing Status
                      </h4>
                    </div>
                    {getEventSummary(meeting.meetingEvents)}
                  </div>
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingsContent;
